// DTOs de C6 — análisis de líneas (ranking de pérdida, coordinador).
//
// Modelo: agregamos vw_cumplimiento_dia_sku_linea por línea en el rango,
// + paros por causal desde registros_paro JOIN causales_paro.
//
// Endpoints:
//   GET /api/coordinador/analisis-lineas?desde&hasta&planta_id&area_id&cliente_id
//   GET /api/coordinador/analisis-lineas/:lineaId/paros?desde&hasta
import { z } from 'zod';
// =============================================================================
// Filtros
// =============================================================================
/** Granularidad temporal — V1 sin 'hora' (no aplica a paros) ni 'año' (deferred). */
export const analisisLineasGranularidadSchema = z.enum(['dia', 'semana', 'mes']);
/** Criterio de ordenamiento de la tabla detalle. */
export const analisisLineasOrdenSchema = z.enum([
    'horas_paro',
    'unidades_sin_producir',
    'cumplimiento_asc',
]);
export const analisisLineasQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    granularidad: analisisLineasGranularidadSchema.optional().default('dia'),
    ordenar_por: analisisLineasOrdenSchema.optional().default('horas_paro'),
    cliente_id: z.string().uuid().optional(),
    planta_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
    /** Filtro inicial desde drill-down de C1 (highlight + scroll de esa línea). */
    linea_id: z.string().uuid().optional(),
    /** Si true, calcula período anterior simétrico y devuelve `comparativo`. */
    comparar: z
        .union([z.boolean(), z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => v === true || v === 'true')
        .pipe(z.boolean().optional()),
    /**
     * Período de comparación explícito (DN8: "a la misma altura"). Si vienen ambos,
     * reemplazan al período anterior simétrico; los resuelve el selector compartido
     * del FE (mismo corte de la semana/mes anterior) para no comparar 4 días de
     * esta semana contra los 4 últimos días de la anterior.
     */
    comparar_desde: z.string().date().optional(),
    comparar_hasta: z.string().date().optional(),
});
// =============================================================================
// Recursos para los filtros
// =============================================================================
const clienteResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
});
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
const areaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    /** # de líneas activas del área (para mostrar en dropdown filtro). */
    lineas_count: z.number().int().min(0),
});
// =============================================================================
// Fila del ranking — una por línea
// =============================================================================
export const analisisLineaSchema = z.object({
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_nombre: z.string(),
    area_nombre: z.string(),
    /** Suma de minutos de paro / 60 en el rango. */
    horas_paro: z.number().min(0),
    /** Conteo de paros (registros_paro rows con hora_fin). */
    paros_count: z.number().int().min(0),
    cantidad_real: z.number().min(0),
    cantidad_programa: z.number().min(0),
    /**
     * Unidades perdidas POR PARO = Σ(minutos_perdidos × capacidad_estandar_unidades_hora / 60).
     * Atribuible a los paros (estilo OEE), independiente del programa. NO es programa − real
     * (eso es cumplimiento, vive en C5). 0 si la línea/SKU no tiene capacidad configurada.
     */
    unidades_sin_producir: z.number().min(0),
    /**
     * Cumplimiento vs CAPACIDAD instalada: SUM(real)/SUM(meta_acumulada)*100,
     * meta_acumulada = capacidad_estandar_unidades_hora × bloques_capturados.
     * Independiente del programa (coherente con unidades_sin_producir por paro).
     * NULL si la línea/SKU no tiene capacidad configurada. NO es real/programa (eso es C5).
     */
    cumplimiento_pct: z.number().min(0).nullable(),
    bloques_capturados: z.number().int().min(0),
    /** Causal con mayor minutos perdidos en la línea (NULL si sin paros). */
    top_causal_nombre: z.string().nullable(),
    top_causal_minutos: z.number().min(0).nullable(),
    /**
     * Pérdida en pesos por paros (WS-5): Σ(minutos × capacidad/60 × precio_vigente
     * del SKU del registro). Es el valor de venta no producido por los paros.
     * null/ausente si el usuario no puede ver costos.
     */
    perdida_pesos: z.number().nullable().optional(),
});
// =============================================================================
// Totales agregados del set filtrado
// =============================================================================
export const analisisLineasTotalesSchema = z.object({
    /** B1: solo paros CERRADOS cuya categoría afecta disponibilidad. */
    horas_paro_total: z.number().min(0),
    paros_count_total: z.number().int().min(0),
    /** DN1: paros abiertos (sin hora_fin) del mismo scope — se muestran aparte, no suman. */
    paros_en_curso: z.number().int().min(0).optional(),
    unidades_sin_producir_total: z.number().min(0),
    /** Pérdida en pesos por paros del rango (WS-5). null/ausente si no ve costos. */
    perdida_pesos_total: z.number().nullable().optional(),
    cantidad_real_total: z.number().min(0),
    cantidad_programa_total: z.number().min(0),
    cumplimiento_promedio_pct: z.number().min(0).nullable(),
    lineas_evaluadas: z.number().int().min(0),
    /** Top N (3 default) líneas por horas_paro — define el chip rojo del chart. */
    lineas_significativas_count: z.number().int().min(0),
    /** Línea con mayor horas_paro del rango — null si nadie tuvo paros. */
    linea_top_perdida: z
        .object({
        linea_id: z.string().uuid(),
        linea_codigo: z.string(),
        linea_nombre: z.string(),
        horas_paro: z.number().min(0),
        unidades_sin_producir: z.number().min(0),
        cumplimiento_pct: z.number().min(0).nullable(),
    })
        .nullable(),
});
// =============================================================================
// Comparativo período anterior simétrico
// =============================================================================
export const analisisLineasComparativoSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    totales: analisisLineasTotalesSchema,
    /** Delta en horas (actual - anterior). Positivo = peor. */
    delta_horas_paro: z.number(),
    /** Delta absoluto en eventos. */
    delta_paros_count: z.number().int(),
    /** Delta absoluto en unidades sin producir. */
    delta_unidades_sin_producir: z.number(),
    /** Delta puntos porcentuales cumplimiento. null si alguno es null. */
    delta_cumplimiento_pct: z.number().nullable(),
});
// =============================================================================
// Sparkline temporal para los KPI cards (un punto por día del rango)
// =============================================================================
export const analisisLineasSeriePuntoSchema = z.object({
    periodo: z.string(),
    horas_paro: z.number().min(0),
    unidades_sin_producir: z.number().min(0),
    /** Eventos de paro del día (para tooltip del gráfico). */
    paros_count: z.number().int().min(0),
});
// =============================================================================
// Response GET /api/coordinador/analisis-lineas
// =============================================================================
export const coordinadorAnalisisLineasSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    scope: z.object({
        plantas_asignadas: z.array(plantaResumenSchema),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        granularidad: analisisLineasGranularidadSchema,
        ordenar_por: analisisLineasOrdenSchema,
        cliente_id: z.string().uuid().nullable(),
        planta_id: z.string().uuid().nullable(),
        area_id: z.string().uuid().nullable(),
    }),
    /** Última actualización = max(created_at) de registros válidos del tenant. */
    ultima_actualizacion: z.string().nullable(),
    recursos: z.object({
        clientes: z.array(clienteResumenSchema),
        plantas: z.array(plantaResumenSchema),
        areas: z.array(areaResumenSchema),
    }),
    totales: analisisLineasTotalesSchema,
    /** Ordenado según query.ordenar_por (DESC por default, ASC si es cumplimiento). */
    lineas: z.array(analisisLineaSchema),
    /** Serie temporal por día (horas_paro y unidades_sin_producir) — para sparklines. */
    serie_temporal: z.array(analisisLineasSeriePuntoSchema),
    /** Presente solo si query.comparar === true. */
    comparativo: analisisLineasComparativoSchema.nullable(),
});
// =============================================================================
// Drill-down — paros por causal de una línea
// =============================================================================
export const paroPorCausalSchema = z.object({
    causal_id: z.string().uuid(),
    causal_madre_id: z.string().uuid(),
    causal_madre_nombre: z.string(),
    causal_nombre: z.string(),
    /** ID de la categoría en el catálogo `categorias_paro`. Reemplaza el enum `categoria_madre`. */
    categoria_id: z.string().uuid(),
    /** Nombre de la categoría (dinámico — no hardcodeado). */
    categoria_nombre: z.string(),
    /** Color hex de la categoría para visuales. Null si no configurado. */
    categoria_color: z.string().nullable(),
    paros_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
    /** Porcentaje de minutos sobre total del rango. */
    pct_minutos: z.number().min(0).max(100),
});
/** Serie diaria de paros de la línea (para el gráfico temporal del modal). */
export const paroSerieDiaSchema = z.object({
    fecha: z.string().date(),
    paros_count: z.number().int().min(0),
    minutos_totales: z.number().min(0),
});
export const coordinadorAnalisisLineaParosSchema = z.object({
    linea: z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        planta_nombre: z.string(),
    }),
    filtros: z.object({
        desde: z.string().date(),
        hasta: z.string().date(),
        /**
         * true → misma base que el ranking "Pérdida por línea" (B1: solo categorías
         * que afectan disponibilidad); false/ausente → paro total registrado (B2).
         */
        solo_afecta_disponibilidad: z.boolean().optional(),
    }),
    totales: z.object({
        paros_count: z.number().int().min(0),
        minutos_totales: z.number().min(0),
        /** DN1: paros abiertos de la línea en el rango — aparte, no suman. */
        en_curso: z.number().int().min(0).optional(),
    }),
    /** Agrupado por (causal_madre, causal). Ordenado por minutos_totales DESC. */
    causales: z.array(paroPorCausalSchema),
    /** Paros por día en el rango (para barras temporales). Ordenado por fecha ASC. */
    serie_diaria: z.array(paroSerieDiaSchema),
});
//# sourceMappingURL=coordinador_analisis_lineas.js.map